import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Section, Empty } from "@/components/kit";
import SecureImage from "@/components/SecureImage";
import useLoad from "@/lib/useLoad";
import api, { errMsg } from "@/lib/api";
import { uploadPhoto } from "@/pages/client/CheckIn";

export default function Progress() {
  const [photos, reload] = useLoad("/photos");
  const [pose, setPose] = useState("front");
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { await uploadPhoto(file, pose); toast.success("Photo uploaded privately"); reload(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const del = async (p) => { try { await api.delete(`/photos/${p.id}`); reload(); toast.success("Photo deleted permanently"); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader eyebrow="Private" title="Progress photos" testid="progress-title">
        <Select value={pose} onValueChange={setPose}>
          <SelectTrigger className="w-32" data-testid="photo-pose-select"><SelectValue /></SelectTrigger>
          <SelectContent>{["front", "side", "back", "other"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={() => ref.current.click()} disabled={busy} data-testid="photo-upload-btn"><Upload size={15} className="mr-1" />{busy ? "Uploading…" : "Upload photo"}</Button>
        <input ref={ref} type="file" accept="image/*" hidden onChange={onFile} data-testid="photo-upload-input" />
      </PageHeader>
      <div className="flex items-start gap-3 panel p-4 mb-6 text-sm text-slate-400" data-testid="photo-privacy-note">
        <ShieldCheck size={18} className="text-emerald-400 shrink-0" />
        Your photos are never public. Only you and your assigned coach can view them, every coach view is logged, and deleting a photo removes it permanently.
      </div>
      <Section>
        {!photos ? <div className="eyebrow">Loading…</div> : !photos.length ? <Empty text="No photos yet." /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {photos.map((p) => (
              <div key={p.id} className="group relative" data-testid={`photo-card-${p.id}`}>
                <SecureImage photoId={p.id} className="w-full aspect-[3/4] object-cover rounded-md" />
                <div className="flex justify-between items-center mt-1.5 text-xs text-slate-500">
                  <span>{p.taken_on} · {p.pose}</span>
                  <button onClick={() => del(p)} className="hover:text-red-400" data-testid={`photo-delete-${p.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
